import { Component, Optional, Inject } from '@angular/core';
import { PlatformLocation } from '@angular/common';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { BaseComponent } from '../../shared/base-components/base.component';
import { CustomerManageService } from '../shared/customer-manage.service';
import { CustomerIndividualBiometric } from '../models/customer-individual-biometric.model';
import { EyeColorType } from '../models/enums/eye-color-type.enum';
import { HairColorType } from '../models/enums/hair-color-type.enum';
import { GenderType } from '../models/enums/gender-type.enum';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { MvdModalService } from 'app/shared/services/modal/mvd-modal.service';
import { filter, mergeMap, finalize } from 'rxjs/operators';
import { ModalSize } from '../../shared/services/modal/mvd-modal.enum';

export class CustomerAttributeContext {
    constructor(public customerNumberId: number) { }
}

@Component({
    selector: './customer-attributes-modal',
    templateUrl: './customer-attributes-modal.html'
})

export class CustomerAttributeModalComponent extends BaseComponent {
    public attributeForm: UntypedFormGroup;
    public customerEyeColorTypes: any[];
    public customerHairColorTypes: any[];
    public customerGenderTypes: any[];
    public showButtonLoading = false;
    public inches: number[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    public feet: number[] = [1, 2, 3, 4, 5, 6, 7, 8];

    private customerAttributes = new CustomerIndividualBiometric();
    private eyeColorTypes = new EyeColorType();
    private hairColorTypes = new HairColorType();
    private genderTypes = new GenderType();
    private HEIGHT_MINIMUM = 3;
    private WEIGHT_MAXIMUM = 400;
    private INCHES_PER_FOOT = 12;

    constructor(private location: PlatformLocation,
        private fb: UntypedFormBuilder,
        private service: CustomerManageService,
        private dialogRef: MatDialogRef<CustomerAttributeModalComponent>,
        private mvdModalService: MvdModalService,
        @Optional() @Inject(MAT_DIALOG_DATA) private data: CustomerAttributeContext) {
        super();

        location.onPopState(() => {
            this.dialogRef.close();
        });
    }

    public ngOnInit(): void {
        this.attributeForm = this.fb.group({
            'genderTypeId': [null],
            'eyeColorTypeId': [null],
            'hairColorTypeId': [null],
            'heightFT': [null],
            'heightIN': [null],
            'weight': [null, Validators.pattern('^\\d*$')]
        });

        this.customerEyeColorTypes = this.eyeColorTypes.GetActive();
        this.customerHairColorTypes = this.hairColorTypes.GetActive();
        this.customerGenderTypes = this.genderTypes.GetActive();
        this.initCustomerAttributes();
        this.attributeForm.disable();
    }

    private initCustomerAttributes(): void {
        this.service.getCustomerAttributes(this.data.customerNumberId)
            .subscribe(
                result => {
                    this.customerAttributes = result,
                    this.attributeForm.get('genderTypeId').setValue(this.customerAttributes.genderTypeId);
                    this.attributeForm.get('eyeColorTypeId').setValue(this.customerAttributes.eyeColorTypeId);
                    this.attributeForm.get('hairColorTypeId').setValue(this.customerAttributes.hairColorTypeId);
                    this.attributeForm.get('weight').setValue(this.customerAttributes.weight);
                    if (result.height != null) {
                        this.attributeForm.get('heightFT').setValue(Math.floor(this.customerAttributes.height / this.INCHES_PER_FOOT));
                        this.attributeForm.get('heightIN').setValue(Math.floor(this.customerAttributes.height % this.INCHES_PER_FOOT));
                    }

                    this.attributeForm.enable();
                },
                e => this.handleValidationResult(e)
            );
    }

    public cancel(): void {
        this.dialogRef.close();
    }

    private getModel(): CustomerIndividualBiometric {
        const model = Object.assign(new CustomerIndividualBiometric(), this.customerAttributes);
        model.customerNumberId = this.data.customerNumberId;

        model.genderTypeId = this.attributeForm.get('genderTypeId').value;
        model.eyeColorTypeId = this.attributeForm.get('eyeColorTypeId').value;
        model.hairColorTypeId = this.attributeForm.get('hairColorTypeId').value;
        model.weight = this.attributeForm.get('weight').value;
        model.height = (+this.attributeForm.get('heightFT').value * this.INCHES_PER_FOOT) + +this.attributeForm.get('heightIN').value;

        return model;
    }

    public saveAttribute(): void {
        if (this.attributeForm.valid) {
            this.showButtonLoading = true;

            if (this.attributeForm.get('heightIN').value == undefined || this.attributeForm.get('heightIN').value == null) {
                this.attributeForm.get('heightIN').setValue(0);
            }

            let operation = null;

            if (this.attributeForm.get('heightFT').value < this.HEIGHT_MINIMUM || this.attributeForm.get('weight').value > this.WEIGHT_MAXIMUM) {

                operation = this.mvdModalService.warning().title('Confirm')
                    .body(`Height and weight outside parameters. Please verify:
					Height: ${this.attributeForm.get('heightFT').value}'${this.attributeForm.get('heightIN').value}";
					Weight: ${this.attributeForm.get('weight').value}lb.`)
                    .okBtn('Yes').cancelBtn('No').open(ModalSize.Medium).afterClosed()
                    .pipe(
                        filter(result => !!result),
                        mergeMap(() => this.service.saveCustomerAttributes(this.getModel()))
                    );
            } else {
                operation = this.service.saveCustomerAttributes(this.getModel());
            }

            operation.pipe(finalize(() => {
                this.showButtonLoading = false;
            }))
                .subscribe(
                    result => this.dialogRef.close(true),
                    e => this.handleValidationResult(e)
                );
        }
    }
}
